import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw, ChevronDown } from "lucide-react";

const STATUS = {
  pass: { icon: CheckCircle2, cls: "text-emerald-600", label: "PASS", chip: "bg-emerald-100 text-emerald-800" },
  warning: { icon: AlertTriangle, cls: "text-amber-600", label: "WARNING", chip: "bg-amber-100 text-amber-800" },
  blocker: { icon: XCircle, cls: "text-red-600", label: "BLOCKER", chip: "bg-red-600 text-white" },
  not_configured: { icon: Clock, cls: "text-muted-foreground", label: "WAITING", chip: "bg-secondary text-muted-foreground" },
};

export default function AdminReadinessTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/readiness"); setData(r.data); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-6" data-testid="admin-readiness">
      <div className={`rounded-2xl p-6 border-2 ${data.ready ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="readiness-headline">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`font-display text-2xl font-semibold ${data.ready ? "text-emerald-800" : "text-red-800"}`}>
              {data.ready ? "🟢 READY FOR BETA BOOKING" : `🔴 ${data.headline}`}
            </p>
            <p className="text-sm mt-1 text-muted-foreground">
              {data.counts.pass} pass · {data.counts.warning} warning · {data.counts.blocker} blocker ·{" "}
              {data.counts.not_configured} waiting on an external provider · {data.total_checks} checks
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Critical path: {data.critical_path.join(" → ")}
            </p>
          </div>
          <button onClick={load} disabled={busy} className="px-4 py-2 rounded-full border border-border bg-card text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="readiness-refresh">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-run checks
          </button>
        </div>
      </div>

      <Section title="BLOCKING FIRST REAL BOOKING" items={data.blocking} open={open} setOpen={setOpen}
        empty="Nothing is blocking your first real booking." testid="readiness-blocking" />
      <Section title="NON-BLOCKING / FUTURE ENHANCEMENT" items={data.non_blocking} open={open} setOpen={setOpen}
        empty="No outstanding warnings." testid="readiness-non-blocking" />

      <div className="space-y-4">
        {data.groups.map((g) => (
          <div key={g.group} className="bg-card rounded-2xl border border-primary/5 p-5" data-testid={`readiness-group-${g.group.replace(/\W+/g, "-").toLowerCase()}`}>
            <h4 className="font-display font-semibold">{g.group}</h4>
            <div className="mt-3 space-y-1.5">
              {g.checks.map((c) => <Row key={c.key} c={c} open={open} setOpen={setOpen} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, items, empty, open, setOpen, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className="font-display font-semibold text-sm uppercase tracking-wide">{title}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-2">{empty}</p>
      ) : (
        <div className="mt-2 space-y-1.5">{items.map((c) => <Row key={c.key} c={c} open={open} setOpen={setOpen} />)}</div>
      )}
    </div>
  );
}

function Row({ c, open, setOpen }) {
  const s = STATUS[c.status] || STATUS.not_configured;
  const isOpen = open === c.key;
  return (
    <div className="rounded-xl border border-border">
      <button onClick={() => setOpen(isOpen ? null : c.key)} className="w-full flex items-center gap-2.5 p-3 text-left" data-testid={`readiness-check-${c.key}`}>
        <s.icon className={`h-4 w-4 shrink-0 ${s.cls}`} />
        <span className="text-sm font-medium flex-1">{c.label}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${s.chip}`}>{s.label}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="px-3 pb-3 -mt-1 text-sm" data-testid={`readiness-detail-${c.key}`}>
          <p className="text-muted-foreground">{c.detail}</p>
          {c.fix && <p className="mt-2"><strong>To fix / finish:</strong> {c.fix}</p>}
          {!c.blocking && c.status !== "pass" && (
            <p className="mt-1 text-xs text-muted-foreground">This does not block your first real booking.</p>
          )}
        </div>
      )}
    </div>
  );
}
